import { useStore } from '../state/store.ts';

/**
 * The key, plus the filters that change what the map draws. Kept in one corner
 * box because a map you cannot decode is decoration.
 *
 * Closed to begin with, behind a button in the same corner. The key explains a
 * map you only need explained once, and an operator who has left it open on a
 * wall display is paying for it with the top-left of every screen — so the
 * default is the map, and the key is one click away.
 */
export function Legend() {
  const filters = useStore((s) => s.filters);
  const setFilter = useStore((s) => s.setFilter);
  const open = useStore((s) => s.legendOpen);
  const staleDays = useStore((s) => s.session?.snapshotStaleDays ?? null);
  const toggle = useStore((s) => s.toggleLegend);

  if (!open) {
    return (
      <button
        type="button"
        className="legend-open"
        onClick={toggle}
        aria-expanded={false}
        aria-controls="legend"
        title="What the colours and shapes mean, and the map filters"
      >
        Legend
      </button>
    );
  }

  return (
    <div className="legend" id="legend">
      <h4>
        Legend
        <button type="button" className="legend-close" onClick={toggle} aria-label="Hide the legend">
          ×
        </button>
      </h4>
      <div className="row">
        <span className="swatch" style={{ background: 'transparent', boxShadow: '0 0 0 1.5px var(--edge)' }} />
        internet-facing (vmbr0)
      </div>
      <div className="row">
        <span className="swatch" style={{ background: 'var(--ok)' }} />
        running
      </div>
      <div className="row">
        <span className="swatch" style={{ background: 'var(--idle)' }} />
        stopped / template
      </div>
      <div className="row">
        <span className="swatch" style={{ background: 'var(--warn)' }} />
        ZFS pool over 90% or degraded
      </div>
      <div className="row">
        <span className="swatch" style={{ background: 'var(--text-dim)', borderRadius: 2, width: 9, height: 9 }} />
        routes for a segment behind it
      </div>
      {staleDays !== null && (
        <>
          <div className="row">
            <span className="swatch diamond" style={{ background: 'var(--edge)' }} />
            has a snapshot
          </div>
          <div className="row">
            <span className="swatch diamond" style={{ background: 'var(--danger)' }} />
            snapshot older than {staleDays === 1 ? 'a day' : `${staleDays} days`}
          </div>
        </>
      )}
      <div className="row">
        <span
          className="swatch"
          style={{ background: 'transparent', borderTop: '2px dashed var(--text-faint)', borderRadius: 0, height: 0 }}
        />
        extra NIC on another bridge
      </div>
      <p className="legend-note">
        Guests sit behind whatever routes their segment, so a firewall&apos;s network hangs off the
        firewall, not off the host.
      </p>

      <div className="filters">
        <label>
          <input
            type="checkbox"
            checked={filters.hideStopped}
            onChange={(event) => setFilter('hideStopped', event.target.checked)}
          />
          Hide stopped guests
        </label>
        <label>
          <input
            type="checkbox"
            checked={filters.hideTemplates}
            onChange={(event) => setFilter('hideTemplates', event.target.checked)}
          />
          Hide templates
        </label>
        <label>
          <input
            type="checkbox"
            checked={filters.showBridges}
            onChange={(event) => setFilter('showBridges', event.target.checked)}
          />
          Show bridges
        </label>
      </div>
    </div>
  );
}
